import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { GlobalStateContext } from "contexts/GlobalStateContext"

const LIFF_SDK_URL = "https://static.line-scdn.net/liff/edge/2/sdk.js"

let sdkPromise = null
function loadLiffSdk() {
  if (window.liff) return Promise.resolve(window.liff)
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script")
      s.src = LIFF_SDK_URL
      s.async = true
      s.onload = () => resolve(window.liff)
      s.onerror = () => reject(new Error("โหลด LINE SDK ไม่สำเร็จ"))
      document.head.appendChild(s)
    })
  }
  return sdkPromise
}

/**
 * Boot the LIFF app: fetch liffId from the server, init the SDK, force LINE login,
 * then ask /no-session/line-sub/getLineProfile who this LINE user is to us.
 * Every API call from the LIFF pages sends `idToken`; the server verifies it with LINE.
 */
export default function useLiff() {
  const { ax } = useContext(GlobalStateContext)
  const [status, setStatus] = useState("loading") // loading | ready | error
  const [error, setError] = useState(null)
  const [idToken, setIdToken] = useState(null)
  const [profile, setProfile] = useState(null)
  const bootedRef = useRef(false)

  const loadProfile = useCallback(async (token) => {
    const res = await ax.post("/no-session/line-sub/getLineProfile", { IdToken: token }, false)
    if (res.data) setProfile(res.data)
    return res.data
  }, [ax])

  useEffect(() => {
    if (bootedRef.current) return
    bootedRef.current = true
    ;(async () => {
      try {
        const cfg = await ax.post("/no-session/line-sub/getLiffConfig", {}, false)
        if (!cfg.data?.LiffId) throw new Error(cfg.error?.message || "ไม่พบ LIFF_ID")
        const liff = await loadLiffSdk()
        await liff.init({ liffId: cfg.data.LiffId })
        if (!liff.isLoggedIn()) {
          liff.login({ redirectUri: window.location.href })
          return
        }
        const token = liff.getIDToken()
        if (!token) throw new Error("ไม่ได้รับ ID token จาก LINE กรุณาเปิดใหม่")
        setIdToken(token)
        await loadProfile(token)
        setStatus("ready")
      } catch (e) {
        console.error("liff boot error", e)
        setError(e.message || String(e))
        setStatus("error")
      }
    })()
  }, [ax, loadProfile])

  const reloadProfile = useCallback(() => idToken ? loadProfile(idToken) : Promise.resolve(null), [idToken, loadProfile])

  const closeWindow = useCallback(() => {
    if (window.liff?.isInClient?.()) window.liff.closeWindow()
  }, [])

  return { status, error, idToken, profile, reloadProfile, closeWindow, inClient: !!window.liff?.isInClient?.() }
}
